import React, { useMemo, useState } from 'react';
import FormWithEditButton from 'tailwind-components/FormWithEditButton/FormWithEditButton';
import Input from 'tailwind-components/Input/Input';
import InputWithCopyButton from 'tailwind-components/InputWithCopyButton/InputWithCopyButton';
import OptionsDropdownButton from 'tailwind-components/OptionsDropdownButton/OptionsDropdownButton';
import { useSelector } from 'react-redux';
import { Filter, Search } from 'lucide-react';
import { cn } from 'lib/utils';
import {
  STATE_LABELS,
  STATE_LICENSE_API_NAMES
} from 'views/AgentInfo/NonResidenceForm/state-license-helpers';
import Card from 'tailwind-components/Card/Card';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';

const LICENSE_FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'with', label: 'With license' },
  { id: 'without', label: 'Without license' }
];

function getLicenseKey(apiName) {
  return apiName === 'Texas'
    ? `${apiName}_License_Informatio`
    : `${apiName}_License_Number`;
}

const filteredLicenseStates = (STATE_LICENSE_API_NAMES, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return STATE_LICENSE_API_NAMES;
  return STATE_LICENSE_API_NAMES.filter(apiName =>
    STATE_LABELS[apiName].toLowerCase().includes(q)
  );
};

export default function Licenses() {
  const user = useSelector(state => state.user.zohoData);
  const [searchQuery, setSearchQuery] = useState('');
  const [licenseFilter, setLicenseFilter] = useState('all');

  const filteredBySearch = useMemo(
    () => filteredLicenseStates(STATE_LICENSE_API_NAMES, searchQuery),
    [searchQuery]
  );

  const filteredStates = useMemo(() => {
    if (licenseFilter === 'all') return filteredBySearch;
    return filteredBySearch.filter(apiName => {
      const licenseKey = getLicenseKey(apiName);
      const hasLicense = !!user[licenseKey];
      return licenseFilter === 'with' ? hasLicense : !hasLicense;
    });
  }, [filteredBySearch, licenseFilter, user]);

  return (
    <SimpleContainer className='flex flex-col gap-4 lg:h-full lg:min-h-0'>
      <Card
        className='shrink-0'
        title='Resident State License Information'
        withSeparator
        separatorVariant='dashed'
      >
        <FormWithEditButton showButton={false}>
          <Input
            label='State'
            value={user.Resident_Insurance_License_State || ''}
            placeholder='-'
            disabled
            compact
            labelClassName='text-primary'
          />
          <InputWithCopyButton
            label='Licence Number'
            value={user.License_Resident_Insurance_Number || ''}
            placeholder='-'
            disabled
            compact
            labelClassName='text-primary'
          />
        </FormWithEditButton>
      </Card>
      <Card
        className='lg:min-h-0 lg:flex-1 lg:overflow-hidden'
        title='Non-Resident State License Information'
        withSeparator
        separatorVariant='dashed'
      >
        <div className='flex flex-col gap-4 lg:min-h-0 lg:flex-1'>
          <div className='flex w-full shrink-0 flex-wrap items-center gap-2'>
            <div className='relative min-w-[200px] flex-1'>
              <div
                className={cn(
                  'flex h-10 items-center gap-2 rounded-md border border-grey-lighter bg-white px-3 py-2',
                  'transition-[box-shadow,border-color] duration-200',
                  'focus-within:border-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/20'
                )}
              >
                <Search
                  className='size-4 shrink-0 text-grey-light'
                  aria-hidden
                />
                <input
                  type='search'
                  role='searchbox'
                  aria-label='Search licenses by state name'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder='Search by state...'
                  className='flex-1 min-w-0 border-0 bg-transparent p-0 text-black text-(length:--font-size-body) placeholder:text-grey-light outline-none'
                />
              </div>
            </div>
            <OptionsDropdownButton
              options={LICENSE_FILTER_OPTIONS}
              onSelect={setLicenseFilter}
              activeId={licenseFilter}
              buttonIcon={<Filter size={20} />}
              placeholder='Filter'
              buttonVariant='default'
              iconOnly
              defaultId='all'
            />
          </div>
          <div className='show-scrollbar flex flex-col gap-4 pr-0.5 lg:min-h-0 lg:flex-1 lg:overflow-x-hidden lg:overflow-y-auto'>
            {filteredStates.length === 0 ? (
              <p className='m-0 py-2 text-sm text-grey-light'>
                No states match your search.
              </p>
            ) : (
              filteredStates.map(api_name => {
                const licenseKey = getLicenseKey(api_name);

                return (
                  <InputWithCopyButton
                    key={api_name}
                    label={STATE_LABELS[api_name]}
                    value={user[licenseKey] || ''}
                    placeholder='-'
                    disabled
                    compact
                    inLine
                    labelClassName='text-primary py-1'
                  />
                );
              })
            )}
          </div>
        </div>
      </Card>
    </SimpleContainer>
  );
}
